import { useState, useRef, useEffect } from "react";
import { Bot, Send, X, Sparkles, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

export default function AIMentor() {
    const [open, setOpen] = useState(false);
    const [msgs, setMsgs] = useState([
        { role: "assistant", content: "Hi! I'm your LARUTA AI Mentor. I can explain concepts, give hints and help you reflect — but I'll never do restricted assessments for you. What are you working on?" },
    ]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const sessionRef = useRef(null);
    const scrollRef = useRef(null);

    useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs, open]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || streaming) return;
        setInput("");
        setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const res = await fetch(`${API}/ai/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ message: text, session_id: sessionRef.current, ai_mode: "assisted" }),
            });
            const sid = res.headers.get("X-Session-Id");
            if (sid) sessionRef.current = sid;
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                setMsgs((m) => {
                    const copy = [...m];
                    copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
                    return copy;
                });
            }
        } catch {
            setMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "The AI Mentor is unavailable right now. Please try again shortly." };
                return copy;
            });
        } finally { setStreaming(false); }
    };

    return (
        <>
            {!open && (
                <button data-testid="ai-mentor-toggle" onClick={() => setOpen(true)}
                    className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary text-primary-foreground pl-4 pr-5 py-3 shadow-2xl hover:scale-105 transition-transform">
                    <Bot className="h-5 w-5" /> <span className="text-sm font-semibold">AI Mentor</span>
                </button>
            )}
            {open && (
                <div data-testid="ai-mentor-panel" className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[32rem] flex flex-col rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between bg-secondary text-secondary-foreground px-4 py-3">
                        <div className="flex items-center gap-2">
                            <span className="grid h-8 w-8 place-items-center rounded-full bg-primary"><Bot className="h-4 w-4 text-primary-foreground" /></span>
                            <div><p className="text-sm font-semibold leading-none">AI Mentor</p><p className="text-[11px] text-secondary-foreground/60 flex items-center gap-1 mt-0.5"><ShieldCheck className="h-3 w-3" /> Responsible AI</p></div>
                        </div>
                        <button onClick={() => setOpen(false)} data-testid="ai-mentor-close" className="p-1 hover:opacity-70"><X className="h-5 w-5" /></button>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
                        {msgs.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                                    {m.content || <Loader2 className="h-4 w-4 animate-spin" />}
                                </div>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={send} className="flex items-center gap-2 border-t border-border p-3">
                        <input data-testid="ai-mentor-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the AI Mentor…"
                            className="flex-1 rounded-full bg-muted px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
                        <Button type="submit" size="icon" disabled={streaming} className="rounded-full shrink-0" data-testid="ai-mentor-send">
                            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                    </form>
                </div>
            )}
        </>
    );
}
